import React, {
    useCallback,
    useEffect,
    useState,
} from 'react';
import {
    Badge,
    Button,
    Card,
    Col,
    Container,
    Form,
    Modal,
    Row,
} from 'react-bootstrap';
import {
    useNavigate,
    useParams,
} from 'react-router-dom';

import API from '../../api/axios';

function StatusBadge({ status }) {
    const map = {
        PENDING: 'warning',
        APPROVED: 'success',
        REJECTED: 'danger',
    };

    return (
        <Badge bg={map[status] || 'secondary'}>
            {status || '—'}
        </Badge>
    );
}

function FieldView({ label, children }) {
    return (
        <>
            <Form.Label className="fw-semibold mb-2">
                {label}
            </Form.Label>

            <div className="d-flex align-items-center mb-3">
                {children ?? (
                    <span className="text-muted">
                        —
                    </span>
                )}
            </div>
        </>
    );
}

const UnverifiedCompanyForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] =
        useState(true);

    const [error, setError] =
        useState(null);

    const [supplier, setSupplier] =
        useState(null);

    const [
        showConfirm,
        setShowConfirm,
    ] = useState(false);

    const [
        actionType,
        setActionType,
    ] = useState(null);

    const [
        actionLoading,
        setActionLoading,
    ] = useState(false);

    const loadSupplier =
        useCallback(async () => {
            const { data } =
                await API.get(
                    `/supplier/registers/${id}`,
                );

            return data;
        }, [id]);

    useEffect(() => {
        let mounted = true;

        const load = async () => {
            try {
                setLoading(true);
                setError(null);

                const data =
                    await loadSupplier();

                if (!mounted) {
                    return;
                }

                setSupplier(data);
            } catch (err) {
                console.error(err);

                if (mounted) {
                    setError(
                        err?.response?.data?.message ||
                        err?.message ||
                        'Failed to load supplier',
                    );
                }
            } finally {
                if (mounted) {
                    setLoading(false);
                }
            }
        };

        load();

        return () => {
            mounted = false;
        };
    }, [loadSupplier]);

    const openConfirm =
        useCallback(
            (type) => {
                setActionType(type);
                setShowConfirm(true);
            },
            [],
        );

    const closeConfirm =
        useCallback(() => {
            if (actionLoading) {
                return;
            }

            setShowConfirm(false);
            setActionType(null);
        }, [actionLoading]);

    const handleConfirmAction =
        useCallback(async () => {
            if (
                actionType !== 'APPROVE' &&
                actionType !== 'REJECT'
            ) {
                return;
            }

            const nextStatus =
                actionType === 'APPROVE'
                    ? 'APPROVED'
                    : 'REJECTED';

            try {
                setActionLoading(true);

                await API.patch(
                    `/supplier/registers/${id}/status`,
                    {
                        status: nextStatus,
                    },
                );

                setSupplier((prev) =>
                    prev
                        ? {
                            ...prev,
                            status: nextStatus,
                        }
                        : prev,
                );

                setShowConfirm(false);
                setActionType(null);
            } catch (err) {
                console.error(err);

                setError(
                    err?.response?.data?.message ||
                    err?.message ||
                    `Failed to ${actionType.toLowerCase()} supplier`,
                );
            } finally {
                setActionLoading(false);
            }
        }, [
            actionType,
            id,
        ]);

    const formatDateTime =
        useCallback((value) => {
            if (!value) {
                return '—';
            }

            const date =
                new Date(value);

            if (
                Number.isNaN(
                    date.getTime(),
                )
            ) {
                return '—';
            }

            return date.toLocaleString();
        }, []);

    if (loading) {
        return (
            <Container fluid>
                <div className="py-4">
                    Loading...
                </div>
            </Container>
        );
    }

    if (error && !supplier) {
        return (
            <Container fluid>
                <div className="text-danger py-4">
                    {error}
                </div>
            </Container>
        );
    }

    return (
        <Container fluid>
            <Row>
                <Col
                    sm={12}
                    className="mb-4"
                >
                    <div className="d-flex justify-content-between align-items-center">
                        <span className="fw-semibold fs-5">
                            Supplier Verification
                        </span>

                        <Button
                            type="button"
                            variant="secondary"
                            onClick={() =>
                                navigate(
                                    '/unverified-companies',
                                )
                            }
                        >
                            Back
                        </Button>
                    </div>
                </Col>

                {error && (
                    <Col sm={12}>
                        <div className="text-danger mb-3">
                            {error}
                        </div>
                    </Col>
                )}

                <Col md={8}>
                    <Card className="h-100 p-2">
                        <Card.Body>
                            <Row>
                                <Col
                                    sm={6}
                                    md={6}
                                >
                                    <FieldView label="Company Name:">
                                        {supplier?.companyName}
                                    </FieldView>
                                </Col>

                                <Col
                                    sm={6}
                                    md={6}
                                >
                                    <FieldView label="Email:">
                                        {supplier?.email}
                                    </FieldView>
                                </Col>

                                <Col
                                    sm={6}
                                    md={6}
                                >
                                    <FieldView label="Country:">
                                        {supplier?.country}
                                    </FieldView>
                                </Col>

                                <Col
                                    sm={6}
                                    md={6}
                                >
                                    <FieldView label="Request Date:">
                                        {formatDateTime(
                                            supplier?.date,
                                        )}
                                    </FieldView>
                                </Col>

                                <Col
                                    sm={6}
                                    md={6}
                                >
                                    <FieldView label="Website:">
                                        {supplier?.webSite ? (
                                            <a
                                                href={
                                                    supplier.webSite
                                                }
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                {
                                                    supplier.webSite
                                                }
                                            </a>
                                        ) : null}
                                    </FieldView>
                                </Col>

                                <Col
                                    sm={6}
                                    md={6}
                                >
                                    <FieldView label="Tax ID:">
                                        {supplier?.taxId}
                                    </FieldView>
                                </Col>

                                <Col
                                    sm={6}
                                    md={6}
                                >
                                    <FieldView label="Contact Name:">
                                        {supplier?.userName}
                                    </FieldView>
                                </Col>

                                <Col
                                    sm={6}
                                    md={6}
                                >
                                    <FieldView label="Position:">
                                        {supplier?.userPosition}
                                    </FieldView>
                                </Col>

                                <Col
                                    sm={6}
                                    md={6}
                                >
                                    <FieldView label="Phone:">
                                        {supplier?.phonePrefix ||
                                            ''}{' '}
                                        {supplier?.phone ||
                                            ''}
                                    </FieldView>
                                </Col>

                                <Col
                                    sm={6}
                                    md={6}
                                >
                                    <FieldView label="Tax Validation Fileld:">
                                        {supplier?.taxValidationFileld ? (
                                            <a
                                                href={`/files/${supplier.taxValidationFileld}`}
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                {
                                                    supplier.taxValidationFileld
                                                }
                                            </a>
                                        ) : null}
                                    </FieldView>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>

                <Col md={4}>
                    <Card className="h-100 p-2">
                        <Card.Body>
                            <FieldView label="Status:">
                                <StatusBadge
                                    status={
                                        supplier?.status
                                    }
                                />
                            </FieldView>

                            <FieldView label="Agent:">
                                {supplier?.agent}
                            </FieldView>

                            <FieldView label="Updated At:">
                                {formatDateTime(
                                    supplier?.updatedAt,
                                )}
                            </FieldView>
                        </Card.Body>
                    </Card>
                </Col>

                <Col
                    xs={12}
                    className="mt-2"
                >
                    <Card>
                        <Card.Body className="d-flex justify-content-end gap-2">
                            {supplier?.status ===
                                'PENDING' && (
                                    <div className="d-flex gap-2">
                                        <Button
                                            type="button"
                                            className="btn-custom-success"
                                            onClick={() =>
                                                openConfirm(
                                                    'APPROVE',
                                                )
                                            }
                                        >
                                            Approve
                                        </Button>

                                        <Button
                                            type="button"
                                            variant="danger"
                                            onClick={() =>
                                                openConfirm(
                                                    'REJECT',
                                                )
                                            }
                                        >
                                            Reject
                                        </Button>
                                    </div>
                                )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Modal
                show={showConfirm}
                onHide={closeConfirm}
                centered
            >
                <Modal.Header
                    closeButton={
                        !actionLoading
                    }
                >
                    <Modal.Title>
                        Confirm Action
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {actionType ===
                        'APPROVE' && (
                            <p>
                                Are you sure you
                                want to{' '}
                                <strong>
                                    approve
                                </strong>{' '}
                                this supplier?
                            </p>
                        )}

                    {actionType ===
                        'REJECT' && (
                            <p>
                                Are you sure you
                                want to{' '}
                                <strong>
                                    reject
                                </strong>{' '}
                                this supplier?
                            </p>
                        )}
                </Modal.Body>

                <Modal.Footer>
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={
                            closeConfirm
                        }
                        disabled={
                            actionLoading
                        }
                    >
                        Cancel
                    </Button>

                    <Button
                        type="button"
                        variant={
                            actionType ===
                                'REJECT'
                                ? 'danger'
                                : 'success'
                        }
                        onClick={
                            handleConfirmAction
                        }
                        disabled={
                            actionLoading
                        }
                    >
                        {actionLoading
                            ? 'Processing...'
                            : 'Confirm'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default UnverifiedCompanyForm;